/* eslint-disable no-magic-numbers */
import { apiPath } from '@/redux/apiPath';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { Line, NewReaction, NewReactions, AnnotationData } from '@/types/models';
import { ThunkConfig } from '@/types/store';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { getError } from '@/utils/error-report/getError';
import { newReactionSchema } from '@/models/newReactionSchema';
import { pageableSchema } from '@/models/pageableSchema';
import {
  ReactionLineTarget,
  UpdateReactionData,
  UpdateReactionLineData,
} from '@/redux/reactions/reactions.types';
import { REACTIONS_FETCHING_ERROR_PREFIX } from './reactions.constants';

export const getReaction = createAsyncThunk<
  NewReaction | undefined,
  { modelId: number; reactionId: number },
  ThunkConfig
>('reactions/getReaction', async ({ modelId, reactionId }) => {
  try {
    const { data } = await axiosInstanceNewAPI.get<NewReaction>(
      apiPath.getReaction(modelId, reactionId),
    );
    const isDataValid = validateDataUsingZodSchema(data, newReactionSchema);
    return isDataValid ? data : undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: REACTIONS_FETCHING_ERROR_PREFIX }));
  }
});

export const getReactionsForModel = createAsyncThunk<
  Array<NewReaction> | undefined,
  number,
  ThunkConfig
>('reactions/getReactionsForModel', async (modelId: number) => {
  try {
    const { data } = await axiosInstanceNewAPI.get<NewReactions>(
      apiPath.getReactionsForModel(modelId),
    );
    const isDataValid = validateDataUsingZodSchema(data, pageableSchema(newReactionSchema));
    return isDataValid ? data.content : undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: REACTIONS_FETCHING_ERROR_PREFIX }));
  }
});

/**
 * Resolves the endpoint that persists a given line. A reaction owns its own
 * line plus one per reactant, product, modifier and operator, and the API
 * exposes a separate path for each of them.
 */
const getLineApiPath = (
  modelId: number,
  reactionId: number,
  target: ReactionLineTarget,
): string => {
  switch (target.kind) {
    case 'reactant':
      return apiPath.updateReactantLine(modelId, reactionId, target.id);
    case 'product':
      return apiPath.updateProductLine(modelId, reactionId, target.id);
    case 'modifier':
      return apiPath.updateModifierLine(modelId, reactionId, target.id);
    case 'operator':
      return apiPath.updateOperatorLine(modelId, reactionId, target.id);
    default:
      return apiPath.updateReactionLine(modelId, reactionId);
  }
};

export const updateReactionMeta = createAsyncThunk<
  null,
  { reaction: NewReaction; data: UpdateReactionData },
  ThunkConfig
>('reactions/updateReactionMeta', async ({ reaction, data }) => {
  try {
    // The API expects the full reaction body, so unchanged fields are echoed
    // back from the copy we already hold in the store.
    const payload = {
      reactionId: reaction.reactionId,
      sboTerm: reaction.sboTerm,
      name: data.name !== undefined ? data.name : reaction.name,
      notes: data.notes !== undefined ? data.notes : reaction.notes,
      reversible: data.reversible !== undefined ? data.reversible : reaction.reversible,
      z: data.z !== undefined ? data.z : reaction.z,
      processCoordinates: reaction.processCoordinates,
      visibilityLevel: reaction.visibilityLevel,
    };

    await axiosInstanceNewAPI.put(apiPath.updateReaction(reaction.map, reaction.id), payload);
    return null;
  } catch (error) {
    return Promise.reject(getError({ error }));
  }
});

export const updateReactionLine = createAsyncThunk<
  null,
  {
    modelId: number;
    reactionId: number;
    target: ReactionLineTarget;
    line: Line;
    data: UpdateReactionLineData;
  },
  ThunkConfig
>('reactions/updateReactionLine', async ({ modelId, reactionId, target, line, data }) => {
  try {
    // Geometry (segments, arrows) is echoed back unchanged; only the styling
    // fields below are editable here.
    const payload = {
      startArrow: line.startArrow,
      endArrow: line.endArrow,
      segments: line.segments,
      z: line.z,
      width: data.width !== undefined ? data.width : line.width,
      color: data.color !== undefined ? data.color : line.color,
      lineType: data.lineType !== undefined ? data.lineType : line.lineType,
    };

    await axiosInstanceNewAPI.put(getLineApiPath(modelId, reactionId, target), payload);
    return null;
  } catch (error) {
    return Promise.reject(getError({ error }));
  }
});

export const addReactionAnnotation = createAsyncThunk<
  null,
  { modelId: number; reactionId: number; annotationData: AnnotationData },
  ThunkConfig
>('reactions/addReactionAnnotation', async ({ modelId, reactionId, annotationData }) => {
  try {
    await axiosInstanceNewAPI.post(
      apiPath.addReactionAnnotation(modelId, reactionId),
      annotationData,
    );
    return null;
  } catch (error) {
    return Promise.reject(getError({ error }));
  }
});

export const updateReactionAnnotation = createAsyncThunk<
  null,
  { modelId: number; reactionId: number; annotationId: number; annotationData: AnnotationData },
  ThunkConfig
>(
  'reactions/updateReactionAnnotation',
  async ({ modelId, reactionId, annotationId, annotationData }) => {
    try {
      await axiosInstanceNewAPI.put(
        apiPath.updateReactionAnnotation(modelId, reactionId, annotationId),
        annotationData,
      );
      return null;
    } catch (error) {
      return Promise.reject(getError({ error }));
    }
  },
);

export const deleteReactionAnnotation = createAsyncThunk<
  null,
  { modelId: number; reactionId: number; annotationId: number },
  ThunkConfig
>('reactions/deleteReactionAnnotation', async ({ modelId, reactionId, annotationId }) => {
  try {
    await axiosInstanceNewAPI.delete(
      apiPath.deleteReactionAnnotation(modelId, reactionId, annotationId),
    );
    return null;
  } catch (error) {
    return Promise.reject(getError({ error }));
  }
});
